import { useEffect, useRef } from 'react'
import mapboxgl from 'mapbox-gl'

/**
 * Zoom level used when flying to a single selected point — a POI, a listing.
 * Each level halves the visible area, so 18.5 lands on the building rather than
 * the block, which is what makes the marker obviously the thing you clicked.
 */
const POI_FLY_TO_ZOOM = 18.5

interface UseFlyToPositionProps {
  map: React.MutableRefObject<mapboxgl.Map | null>
  mapLoaded: boolean
  flyToPosition?: [number, number]
  /** Zoom to fly to; defaults to the POI zoom. */
  flyToZoom?: number
  isFlyingToRef: React.MutableRefObject<boolean>
  isPublic?: boolean
  maxBbox?: mapboxgl.LngLatBoundsLike
}

/**
 * Hook to handle flyTo animation without relaxing public navigation bounds.
 */
export const useFlyToPosition = ({
  map,
  mapLoaded,
  flyToPosition,
  flyToZoom = POI_FLY_TO_ZOOM,
  isFlyingToRef,
  isPublic = false,
  maxBbox,
}: UseFlyToPositionProps) => {
  const flyToTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const instance = map.current
    if (!instance || !mapLoaded || !flyToPosition) return

    if (!Number.isFinite(flyToPosition[0]) || !Number.isFinite(flyToPosition[1])) {
      console.warn('useFlyToPosition: Invalid coordinates', flyToPosition)
      return
    }

    isFlyingToRef.current = true

    // Authenticated maps retain their existing fly-to behavior. Public maps must
    // never drop their address bounds, even temporarily or for distant markers.
    const boundsToRestore = isPublic ? null : instance.getMaxBounds()
    let restoreTimeout: ReturnType<typeof setTimeout> | undefined
    const onMoveEnd = () => {
      if (boundsToRestore) {
        restoreTimeout = setTimeout(() => {
          if (map.current === instance) instance.setMaxBounds(boundsToRestore)
        }, 100)
      }
    }
    const onMoveStart = () => instance.once('moveend', onMoveEnd)

    if (boundsToRestore) instance.setMaxBounds(null)

    // Wait a tick for an authenticated bounds removal to settle before flying.
    const startTimeout = setTimeout(() => {
      if (map.current !== instance) return
      if (boundsToRestore) instance.once('movestart', onMoveStart)
      instance.flyTo({
        center: flyToPosition,
        zoom: flyToZoom,
        duration: 1000,
        essential: true,
      })
    }, 50)

    // Prevent bbox fitting from interrupting the flight or immediately recentering.
    flyToTimeoutRef.current = setTimeout(() => {
      isFlyingToRef.current = false
      flyToTimeoutRef.current = null
    }, 3000)

    return () => {
      clearTimeout(startTimeout)
      clearTimeout(restoreTimeout)
      if (flyToTimeoutRef.current) clearTimeout(flyToTimeoutRef.current)
      instance.off('movestart', onMoveStart)
      instance.off('moveend', onMoveEnd)
      // Restore interrupted authenticated flights only if no newer area was applied.
      if (boundsToRestore && map.current === instance && !instance.getMaxBounds()) {
        instance.setMaxBounds(boundsToRestore)
      }
      isFlyingToRef.current = false
    }
  }, [flyToPosition, flyToZoom, mapLoaded, isPublic, maxBbox, map, isFlyingToRef])
}
